/**
 * Pure formatting / sorting / grouping helpers for the System Status connection
 * roster (Flow L Frame 3; ADR-035 / S6.5 + S6.5a).
 *
 * No React — kept separate from :file:`./status-connection-list` so that module
 * exports only components (react-refresh/only-export-components) and so these
 * are unit-testable without the DOM.
 *
 * Cross-references:
 *   - Flow L §5 (connection list); storyboard OQ-3 (virtualize / worst-first /
 *     group-by-aggregator).
 *   - REQ-STATUS-016 (unknown never a false green — reflected in the priority).
 *   - ConnectionModel.aggregator_id (the BFF aggregator-store UUID; null for
 *     direct-producer / egress connections).
 */
import {
  parseRollupState,
  worstRollupState,
  type RollupState,
  type StatusConnection,
} from "@/system-status/status-model";

/**
 * Display priority for the worst-first ordering — most actionable at the top
 * (unhealthy → degraded → unknown → healthy). Never surfaces a healthy row /
 * group above a non-healthy one (REQ-STATUS-016 spirit).
 */
const CONNECTION_PRIORITY: Record<string, number> = {
  unhealthy: 3,
  degraded: 2,
  unknown: 1,
  healthy: 0,
};

/**
 * Format an ISO timestamp / epoch as a UTC ``HH:MM:SSZ`` time-of-day, or a
 * fallback for null / unparseable input. The compact "as of" / history marker
 * shared by the System Status detail (Frame 2/3) — kept here (pure module) so the
 * entity-details section files reuse one formatter (CLAUDE.md §4.4).
 */
export function formatStatusTimestamp(
  value: string | number | null | undefined,
  fallback = "—",
): string {
  if (value === null || value === undefined) return fallback;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  return `${date.toISOString().slice(11, 19)}Z`;
}

/** Sort connections worst-first (most actionable at the top), then by peer label. */
export function sortConnectionsWorstFirst(
  connections: readonly StatusConnection[],
): StatusConnection[] {
  return [...connections].sort((a, b) => {
    const pa = CONNECTION_PRIORITY[parseRollupState(a.rollup_state)] ?? 1;
    const pb = CONNECTION_PRIORITY[parseRollupState(b.rollup_state)] ?? 1;
    if (pa !== pb) return pb - pa;
    return a.peer_label.localeCompare(b.peer_label);
  });
}

/** The bucket label for the connections that carry no aggregator id. */
export const UNGROUPED_LABEL = "Direct / ungrouped";

/** One aggregator's connections (or the direct/ungrouped bucket). */
export interface ConnectionGroup {
  /** The aggregator-store UUID, or null for the direct-producer / egress bucket. */
  aggregatorId: string | null;
  /** Display label — a representative peer, or the ungrouped-bucket label. */
  label: string;
  /** The group's connections, worst-first. */
  connections: StatusConnection[];
  /** Worst-of the group's states (drives group ordering + the header dot). */
  worstState: RollupState;
}

/**
 * Group connections by ``aggregator_id`` (storyboard OQ-3 group-by-aggregator).
 * Connections sharing an aggregator cluster under one group; those with
 * ``aggregator_id === null`` fall into the direct/ungrouped bucket. Groups are
 * ordered **worst-first** (worst group first; real aggregators before the
 * ungrouped bucket at equal severity, then by label), and connections within a
 * group are worst-first.
 */
export function groupConnectionsByAggregator(
  connections: readonly StatusConnection[],
): ConnectionGroup[] {
  const buckets = new Map<string | null, StatusConnection[]>();
  for (const c of connections) {
    const key = c.aggregator_id ?? null;
    const arr = buckets.get(key);
    if (arr) arr.push(c);
    else buckets.set(key, [c]);
  }

  const groups: ConnectionGroup[] = [];
  for (const [aggregatorId, conns] of buckets) {
    const sorted = sortConnectionsWorstFirst(conns);
    const worstState = worstRollupState(sorted.map((c) => parseRollupState(c.rollup_state)));
    // A stable representative label (not the worst-dependent first row).
    const label =
      aggregatorId === null
        ? UNGROUPED_LABEL
        : [...conns].map((c) => c.peer_label).sort((a, b) => a.localeCompare(b))[0];
    groups.push({ aggregatorId, label, connections: sorted, worstState });
  }

  groups.sort((a, b) => {
    const pa = CONNECTION_PRIORITY[a.worstState] ?? 1;
    const pb = CONNECTION_PRIORITY[b.worstState] ?? 1;
    if (pa !== pb) return pb - pa;
    // At equal severity, real aggregators before the ungrouped bucket, then label.
    const na = a.aggregatorId === null ? 1 : 0;
    const nb = b.aggregatorId === null ? 1 : 0;
    if (na !== nb) return na - nb;
    return a.label.localeCompare(b.label);
  });
  return groups;
}

/** A flattened roster row — a group header or one of its connections. */
export type RosterRow =
  { kind: "group"; group: ConnectionGroup } | { kind: "connection"; connection: StatusConnection };

/**
 * Flatten grouped connections into the row list the virtual list renders —
 * ``[header, conn, conn, header, conn, …]`` — so the in-house fixed-row
 * :func:`VirtualList` still backs the (uniform-height) rows.
 */
export function flattenGroups(groups: readonly ConnectionGroup[]): RosterRow[] {
  const rows: RosterRow[] = [];
  for (const group of groups) {
    rows.push({ kind: "group", group });
    for (const connection of group.connections) rows.push({ kind: "connection", connection });
  }
  return rows;
}

/** Humanize a byte count (e.g. ``4.2 GB``). */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${Math.round(bytes)} B`;
  const units = ["KB", "MB", "GB", "TB", "PB"];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(1)} ${units[unit]}`;
}
